import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  DateRangePicker,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@jem-open/jem-ui";
// sonner is a transitive dependency of @jem-open/jem-ui (peer dep), used here for imperative toast calls
// eslint-disable-next-line import/no-extraneous-dependencies
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import { Download, ExternalLink } from "lucide-react";
import { ReportScope } from "../services/model/ReportScope";
import { ReportType } from "../services/model/ReportType";
import { filenameFor } from "../utils/filename";
import { formatShortDate } from "../utils/formatDate";
import {
  DATE_PRESET_OPTIONS,
  DatePreset,
  rangeForPreset,
} from "../utils/datePresets";
import useAvailableShiftsForScope from "../hooks/useAvailableShiftsForScope";
import {
  formatIsoDate,
  triggerBlobDownload,
  usePlannedVsActualsReportApiService,
  usePostingSheetReportApiService,
  useTimesheetReportApiService,
} from "../services/api/ReportGenerationApiService";
import ScopeSummary from "./ScopeSummary";
import ReportTypePicker from "./ReportTypePicker";
import ShiftTypeSelector from "./ShiftTypeSelector";

export interface ReportWizardBodyProps {
  scope: ReportScope;
  /** Called when a download is triggered. Parent can choose to close the dialog. */
  onDownloadComplete?: (args: {
    reportType: ReportType;
    scope: ReportScope;
    filename?: string;
  }) => void;
  /** Provide a cancel callback to render the "Cancel" button. */
  onCancel?: () => void;
}

const ReportWizardBody = ({
  scope,
  onDownloadComplete,
  onCancel,
}: ReportWizardBodyProps): JSX.Element => {
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>("7d");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    rangeForPreset("7d")
  );
  const [singleDate, setSingleDate] = useState<Date | undefined>(new Date());
  /** null = "All shifts" for the date-range reports. Required (non-null) for posting sheet. */
  const [shiftName, setShiftName] = useState<string | null>(null);

  const { shifts, isLoading: isLoadingShifts } =
    useAvailableShiftsForScope(scope);

  const plannedVsActuals = usePlannedVsActualsReportApiService();
  const timesheet = useTimesheetReportApiService();
  const postingSheet = usePostingSheetReportApiService();
  const isGenerating =
    plannedVsActuals.isLoading || timesheet.isLoading || postingSheet.isLoading;

  useEffect(() => {
    if (reportType === "posting-sheet") {
      // Posting sheet requires a specific shift — default to the first
      // available config (or null if none loaded yet; user will see the
      // empty-state helper text).
      setShiftName((current) => {
        if (current && shifts.some((s) => s.name === current)) return current;
        return shifts[0]?.name ?? null;
      });
    } else if (
      reportType === "planned-vs-actuals" ||
      reportType === "timesheet"
    ) {
      setShiftName(null);
    }
  }, [reportType, shifts]);

  // Posting Sheet is single-day / single-location and isn't offered for region
  // (Site Group) scope. If it was selected before the user switched to a
  // region, clear the now-hidden selection so the wizard stays consistent.
  useEffect(() => {
    if (scope.kind === "region" && reportType === "posting-sheet") {
      setReportType(null);
    }
  }, [scope.kind, reportType]);

  const handlePresetChange = (preset: DatePreset): void => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange(rangeForPreset(preset));
    }
  };

  const ctaLabel = "Download";

  const successMessage = useMemo(() => {
    if (!reportType) return "Report ready";
    if (reportType === "planned-vs-actuals") {
      return `Planned vs Actuals downloaded for ${scope.label}`;
    }
    if (reportType === "timesheet") {
      return `Timesheet downloaded for ${scope.label}`;
    }
    return `Posting Sheet downloaded for ${scope.label}`;
  }, [reportType, scope]);

  const dateRangeReady =
    dateRange?.from !== undefined && dateRange?.to !== undefined;

  const canSubmit =
    reportType !== null &&
    (reportType === "posting-sheet"
      ? singleDate !== undefined && shiftName !== null
      : dateRangeReady);

  const handleSubmit = (): void => {
    if (!reportType || !canSubmit) return;
    const filename = filenameFor(
      reportType,
      scope,
      dateRange,
      singleDate,
      shiftName
    );

    const onSuccess = (blob: Blob): void => {
      triggerBlobDownload(blob, filename);
      toast.success(successMessage, { description: `File: ${filename}` });
      onDownloadComplete?.({ reportType, scope, filename });
    };

    const onError = (): void => {
      toast.error("Report generation failed", {
        description: "The service returned an error. Please try again.",
      });
    };

    const scopePayload = { kind: scope.kind, id: scope.id };

    if (reportType === "posting-sheet" && singleDate && shiftName) {
      postingSheet.mutate(
        {
          scope: scopePayload,
          date: formatIsoDate(singleDate),
          shift_name: shiftName,
        },
        { onSuccess, onError }
      );
      return;
    }

    if (!dateRange?.from || !dateRange?.to) return;
    const body = {
      scope: scopePayload,
      date_from: formatIsoDate(dateRange.from),
      date_to: formatIsoDate(dateRange.to),
      shift_name: shiftName,
    };

    if (reportType === "planned-vs-actuals") {
      plannedVsActuals.mutate(body, { onSuccess, onError });
    } else if (reportType === "timesheet") {
      timesheet.mutate(body, { onSuccess, onError });
    }
  };

  const handlePreview = (): void => {
    if (!reportType || !canSubmit) return;
    const params = new URLSearchParams({
      preview: "1",
      type: reportType,
      scopeKind: scope.kind,
      scopeId: scope.id,
      scopeLabel: scope.label,
      shift: shiftName ?? "",
    });
    if (scope.kind === "site" && scope.region)
      params.set("scopeRegion", scope.region);
    if (scope.kind === "roster" && scope.parentSite)
      params.set("scopeParent", scope.parentSite);
    if (reportType === "posting-sheet" && singleDate) {
      params.set("date", formatIsoDate(singleDate));
    } else if (dateRange?.from && dateRange?.to) {
      params.set("dateFrom", formatIsoDate(dateRange.from));
      params.set("dateTo", formatIsoDate(dateRange.to));
    }
    const url = `/reports/preview?${params.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const needsDateRange =
    reportType === "planned-vs-actuals" || reportType === "timesheet";
  const needsSingleDate = reportType === "posting-sheet";

  return (
    <div className="flex flex-col gap-md">
      <ScopeSummary scope={scope} />

      <ReportTypePicker
        value={reportType}
        onChange={setReportType}
        hiddenTypes={scope.kind === "region" ? ["posting-sheet"] : []}
      />

      {reportType && (
        <div className="rounded-lg border border-greyscale-border bg-white p-md space-y-md">
          <p className="text-sm font-semibold text-greyscale-text-title">
            Filters
          </p>

          {needsDateRange && (
            <div>
              <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
                Date range
              </Label>
              <div className="flex flex-col sm:flex-row gap-xs">
                <Select
                  value={datePreset}
                  onValueChange={(v) => handlePresetChange(v as DatePreset)}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESET_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {datePreset === "custom" && (
                  <div className="flex-1">
                    <DateRangePicker
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                      placeholder="Pick a date range"
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                {datePreset === "custom"
                  ? "Pick any range."
                  : `${formatShortDate(dateRange?.from)} – ${formatShortDate(
                      dateRange?.to
                    )}`}
              </p>
            </div>
          )}

          {needsSingleDate && (
            <div>
              <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
                Date
              </Label>
              <div className="w-full sm:w-[220px]">
                <DatePicker
                  date={singleDate}
                  onDateChange={setSingleDate}
                  placeholder="Pick a date"
                />
              </div>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                Posting Sheet covers a single day.
              </p>
            </div>
          )}

          <ShiftTypeSelector
            value={shiftName}
            onChange={setShiftName}
            allowAll={reportType !== "posting-sheet"}
            options={shifts}
            isLoading={isLoadingShifts}
          />
        </div>
      )}

      {isGenerating && (
        <div className="space-y-xs">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-sm pt-md mt-xs border-t border-greyscale-border">
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isGenerating}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handlePreview}
          disabled={!canSubmit || isGenerating}
          leftIcon={<ExternalLink size={16} />}
          className="w-full sm:w-auto"
        >
          Preview
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isGenerating}
          leftIcon={<Download size={16} />}
          className="w-full sm:w-auto"
        >
          {isGenerating ? "Generating…" : ctaLabel}
        </Button>
      </div>
    </div>
  );
};

export default ReportWizardBody;
